import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Dices, Target, Sparkles } from 'lucide-react'
import {
	Card,
	CardContent,
	CardFooter,
	CardHeader,
	CardTitle,
} from '@/common/ui/card'
import { Badge } from '@/common/ui/badge'
import { Button } from '@/common/ui/button'
import { Skeleton } from '@/common/ui/skeleton'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/common/ui/alert-dialog'
import { Separator } from '@/common/ui/separator'
import useQueryGetFlexMessagePresets from '../../api/hooks/useQueryGetFlexMessagePresets'
import useMutationDuplicateFlexMessagePreset from '../../api/hooks/useMutationDuplicateFlexMessagePreset'
import { settingsKeys } from '../../api/keys'
import type { FlexMessagePreset } from '../../types'

function PresetCard({ preset }: { preset: FlexMessagePreset }) {
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const [isLoading, setIsLoading] = useState(false)
	const [showConfirm, setShowConfirm] = useState(false)

	const { mutateAsync } = useMutationDuplicateFlexMessagePreset()

	const handleDuplicate = async () => {
		setShowConfirm(false)
		setIsLoading(true)
		try {
			const result = await mutateAsync(preset.id)
			await queryClient.invalidateQueries({
				queryKey: settingsKeys.flexHotGames(),
			})
			toast.success('สร้าง Flex Message จากเทมเพลตสำเร็จ')
			navigate(`/operator/settings/flex-hot-games/edit/${result.data.id}`)
		} catch {
			toast.error('ไม่สามารถสร้าง Flex Message จากเทมเพลตได้')
		} finally {
			setIsLoading(false)
		}
	}

	const randomCount = preset.games.filter((g) => g.isRandomized).length
	const fixedCount = preset.games.filter((g) => !g.isRandomized).length

	return (
		<>
			<Card className="flex flex-col transition-shadow hover:shadow-md">
				<CardHeader className="pb-3">
					<CardTitle className="flex items-center gap-2 text-base">
						<Sparkles className="h-4 w-4 text-primary" />
						{preset.name}
					</CardTitle>
					<p className="text-sm text-muted-foreground">{preset.description}</p>
				</CardHeader>

				<CardContent className="flex-1 pb-3">
					<div className="flex items-center gap-2 text-sm text-muted-foreground">
						<span>{preset.games.length} เกม</span>
						{randomCount > 0 && (
							<Badge variant="secondary" className="gap-1 text-xs">
								<Dices className="h-3 w-3" />
								สุ่ม {randomCount}
							</Badge>
						)}
						{fixedCount > 0 && (
							<Badge variant="secondary" className="gap-1 text-xs">
								<Target className="h-3 w-3" />
								กำหนด {fixedCount}
							</Badge>
						)}
					</div>
				</CardContent>

				<CardFooter>
					<Button
						className="w-full"
						onClick={() => setShowConfirm(true)}
						disabled={isLoading}
					>
						{isLoading ? 'กำลังสร้าง...' : 'ใช้เทมเพลตนี้'}
					</Button>
				</CardFooter>
			</Card>

			<AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>ยืนยันใช้เทมเพลต</AlertDialogTitle>
						<AlertDialogDescription>
							จะสร้าง Flex Message ใหม่จากเทมเพลต &quot;{preset.name}&quot;
							โดยอัตโนมัติ สามารถแก้ไขเพิ่มเติมได้ภายหลัง (Flex Message
							ที่ถูกสร้างใหม่จะยังไม่ถูกเปิดใช้งาน)
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter className="gap-2">
						<AlertDialogCancel>ยกเลิก</AlertDialogCancel>
						<AlertDialogAction onClick={handleDuplicate}>
							ยืนยัน
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}

function PresetCardSkeleton() {
	return (
		<Card className="flex flex-col">
			<CardHeader className="pb-3">
				<Skeleton className="h-5 w-3/4" />
				<Skeleton className="h-4 w-full" />
			</CardHeader>
			<CardContent className="flex-1 pb-3">
				<div className="flex flex-wrap gap-1.5">
					<Skeleton className="h-5 w-16" />
					<Skeleton className="h-5 w-20" />
					<Skeleton className="h-5 w-14" />
				</div>
			</CardContent>
			<CardFooter>
				<Skeleton className="h-9 w-full" />
			</CardFooter>
		</Card>
	)
}

export default function FlexMessagePresetGallery() {
	const { data, isLoading } = useQueryGetFlexMessagePresets()
	const presets = data?.data ?? []

	return (
		<div className="space-y-4">
			<div>
				<h3 className="text-base font-semibold tracking-tight sm:text-lg">
					เทมเพลต Flex Message
				</h3>
				<p className="mt-1 text-sm text-muted-foreground">
					เลือกเทมเพลตเพื่อสร้าง Flex Message สำเร็จรูป
					แล้วปรับแต่งเพิ่มเติมได้ตามต้องการ
				</p>
			</div>
			<Separator />

			{isLoading ? (
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
					{Array.from({ length: 6 }).map((_, i) => (
						<PresetCardSkeleton key={i} />
					))}
				</div>
			) : presets.length === 0 ? (
				<div className="flex h-32 items-center justify-center">
					<p className="text-muted-foreground">ไม่พบเทมเพลต</p>
				</div>
			) : (
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
					{presets.map((preset) => (
						<PresetCard key={preset.id} preset={preset} />
					))}
				</div>
			)}
		</div>
	)
}
